<!--
 * @Author: lixinxin lixinxin@huan.tv
 * @Date: 2023-03-09 11:30:29
 * @LastEditors: 文件最后更新人：任亮
 * @LastEditTime: 2025-10-16 16:00:58
 * @FilePath: \operation-web\src\App.vue
 * @Description: 
 * 
 * Copyright (c) 2023 by ${git_name_email}, All Rights Reserved. 
-->
<script setup lang="ts">
import { NConfigProvider, GlobalThemeOverrides } from "naive-ui";
import NotificationApi from "@/components/notification-api/index.vue";
import MessageApi from "@/components/message-api/index.vue";
import { onBeforeMount, onMounted, onBeforeUnmount } from "vue";

const themeOverrides: GlobalThemeOverrides = {
  common: {
    primaryColor: "#1E4EEC",
    primaryColorHover: "#1E4EEC",
    primaryColorPressed: "#1E4EEC",
    textColor2: "#262626",
    // closeColorHover: "#595959",
  },
  Button: {
    textColor: "#c5c5c5",
    textColorText: "#666",
    textColorHover: "#1E4EEC",
    paddingRoundMedium: "0 10px",
    iconMarginMedium: "2px",
    colorInfo: "#1E4EEC",
    colorHoverInfo: "#1E4EEC",
  },
  Tag: {
    borderRadius: "5px",
  },
  Form: {
    feedbackFontSizeMedium: "12px",
    labelTextColor: "#999999",
  },
  Dialog: {
    iconColorInfo: "#1E4EEC",
    padding: "16px 24px 16px 24px",
    titleFontSize: "16px",
    contentMargin: "24px 0 16px 0",
  },
  Drawer: {
    titleFontSize: "16px",
  },
};
// const preventTouchpadBack = (event: WheelEvent) => {
//   if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
//     return;
//   }
//   const doc = document.documentElement;
//   const atLeftEdge = window.scrollX <= 0 && event.deltaX < 0;
//   const atRightEdge =
//     window.scrollX + window.innerWidth >= doc.scrollWidth && event.deltaX > 0;
//   if (atLeftEdge || atRightEdge) {
//     event.preventDefault();
//   }
// };

// onMounted(() => {
//   window.addEventListener("wheel", preventTouchpadBack, { passive: false });
// });

// onBeforeUnmount(() => {
//   window.removeEventListener("wheel", preventTouchpadBack);
// });

// 移动端键盘弹出时防止页面缩回的解决方案
const initKeyboardFix = (): (() => void) | null => {
  // 检测是否为移动设备
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile) return null;

  let initialViewportHeight = window.innerHeight;
  let isKeyboardOpen = false;
  let fixedHeight: string | null = null;

  // 保存原始视口高度
  const saveViewportHeight = () => {
    initialViewportHeight = window.innerHeight;
    // 注意：不要在移动端输入时强行改 body overflow/height，这会导致 iOS/部分安卓丢焦点，键盘立刻收起
    // 这里仅记录高度，用于判断键盘状态
    if (!fixedHeight) {
      fixedHeight = `${initialViewportHeight}px`;
    }
  };

  // 恢复视口高度
  const restoreViewportHeight = () => {
    fixedHeight = null;
  };

  // 方案3：监听输入框聚焦事件
  const handleFocusIn = (e: FocusEvent) => {
    const target = e.target as HTMLElement;
    // 检查是否为输入框、文本域或可编辑元素
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      // 延迟执行，等待键盘弹出
      setTimeout(() => {
        if (!isKeyboardOpen) {
          isKeyboardOpen = true;
          saveViewportHeight();
        }
      }, 100);
    }
  };

  const handleFocusOut = (e: FocusEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      // 延迟执行，等待键盘收起
      setTimeout(() => {
        // 检查是否还有其他输入框聚焦
        const activeElement = document.activeElement as HTMLElement;
        if (
          !activeElement ||
          (activeElement.tagName !== 'INPUT' &&
            activeElement.tagName !== 'TEXTAREA' &&
            !activeElement.isContentEditable)
        ) {
          isKeyboardOpen = false;
          restoreViewportHeight();
        }
      }, 300);
    }
  };

  // 方案2：使用 visualViewport API 监听键盘状态（更精确）
  const handleVisualViewportResize = () => {
    if (window.visualViewport) {
      const viewport = window.visualViewport;
      const heightDiff = initialViewportHeight - viewport.height;
      
      // 如果视口高度减少超过 150px，认为键盘已弹出
      if (heightDiff > 150 && !isKeyboardOpen) {
        isKeyboardOpen = true;
        saveViewportHeight();
      } else if (heightDiff < 50 && isKeyboardOpen) {
        // 如果视口高度恢复，认为键盘已收起
        isKeyboardOpen = false;
        restoreViewportHeight();
        // 更新初始高度
        initialViewportHeight = window.innerHeight;
      }
    }
  };

  // 监听窗口大小变化（备用方案）
  const handleResize = () => {
    if (!window.visualViewport) {
      const currentHeight = window.innerHeight;
      const heightDiff = initialViewportHeight - currentHeight;
      
      if (heightDiff > 150 && !isKeyboardOpen) {
        isKeyboardOpen = true;
        saveViewportHeight();
      } else if (heightDiff < 50 && isKeyboardOpen) {
        isKeyboardOpen = false;
        restoreViewportHeight();
        initialViewportHeight = currentHeight;
      }
    }
  };

  // 绑定事件监听器
  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('focusout', handleFocusOut);
  
  // 优先使用 visualViewport API（iOS Safari 13+、Android Chrome 61+）
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleVisualViewportResize);
  } else {
    // 降级方案：使用 window resize 事件
    window.addEventListener('resize', handleResize);
  }

  // 清理函数
  return () => {
    document.removeEventListener('focusin', handleFocusIn);
    document.removeEventListener('focusout', handleFocusOut);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
    } else {
      window.removeEventListener('resize', handleResize);
    }
    restoreViewportHeight();
  };
};

let cleanupKeyboardFix: (() => void) | null = null;

onMounted(() => {
  cleanupKeyboardFix = initKeyboardFix();
});

onBeforeUnmount(() => {
  if (cleanupKeyboardFix) {
    cleanupKeyboardFix();
  }
});
</script>
<template>
  <n-message-provider>
    <message-api></message-api>
  </n-message-provider>
  <n-notification-provider>
    <notification-api></notification-api>
  </n-notification-provider>
  <n-config-provider :theme-overrides="themeOverrides">
    <div>
      <router-view />
      <!-- 清除滚动时select下拉框设置 -->
      <a href="#" id="hide_focus"></a>
    </div>
  </n-config-provider>
</template>

<style>
::-webkit-scrollbar-track-piece {
  background-color: #f8f8f8;
}

::-webkit-scrollbar {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

::-webkit-scrollbar-thumb {
  background-color: #dddddd;
  background-clip: padding-box;
  min-height: 28px;
  -webkit-border-radius: 2em;
  -moz-border-radius: 2em;
  border-radius: 2em;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #bbb;
}

input:focus::-webkit-contacts-auto-fill-button {
  opacity: 0;
}

.text-orange-left {
  font-size: var(--theme-font-size);
  font-family: PingFangSC-Regular, PingFang SC;
  font-weight: 400;
  color: #1e4eec;
  cursor: pointer;
  margin-left: 10px;
}

/*部分样式调整，如果后续不使用嵌套方案将移除*/
@media screen and (min-width: 300px) and (max-width: 960px) {
  /* .layout-navigation{
    display: none !important;
  } */
}

@media screen and (orientation: landscape) {}

#hide_focus {
  opacity: 0;
  pointer-events: none;
  position: fixed;
  bottom: 0;
}
</style>
